<script setup lang="ts">
import { computed } from 'vue';
import { BFormTextarea } from 'bootstrap-vue-next';
import { required as requiredRule } from '@regle/rules';
import { useItpFormControl, useItpFormField } from './useItpForm';

const props = withDefaults(
  defineProps<{
    modelValue?: string | string[] | null;
    name: string;

    id?: string;
    placeholder?: string;
    size?: 'sm' | 'md' | 'lg';

    rows?: number;
    maxRows?: number;
    resize?: 'vertical' | 'horizontal' | 'none';
    monospace?: boolean;

    autoGrow?: boolean;

    autocomplete?: boolean | string;

    disabled?: boolean;
    readonly?: boolean;
    required?: boolean;

    debounce?: number | string;

    mode?: 'text' | 'list';
  }>(),
  {
    rows: 3,
    autoGrow: true,
  },
);

const emit = defineEmits<{
  (event: 'update:modelValue', value: string | string[] | null): void;
  (event: 'change', value: string | string[] | null): void;
  (event: 'input', value: string | string[] | null): void;
}>();

const { isDisabled, isReadonly } = useItpFormControl(props);

const { touch } = useItpFormField(props, (isRequired) =>
  isRequired ? { required: requiredRule } : {},
);

const autocompleteAttr = computed(() => {
  const autocomplete = props.autocomplete;
  if (typeof autocomplete === 'boolean') {
    return autocomplete ? 'on' : 'off';
  }
  return autocomplete;
});

const textareaClass = computed(() => [
  `itp-textarea--resize-${props.resize ?? 'vertical'}`,
  {
    'itp-textarea--auto-grow': props.autoGrow,
    monospace: props.monospace,
  },
]);

const modelProxy = computed({
  get(): string {
    if (props.mode === 'list') {
      const value = props.modelValue;
      return Array.isArray(value) && value.length ? value.join('\n') : '';
    }
    return typeof props.modelValue === 'string' ? props.modelValue : '';
  },
  set(next: string) {
    let out: string | string[] = next;

    if (props.mode === 'list') {
      const trimmed = next.trim();
      out = trimmed ? trimmed.split('\n') : [];
    }

    emit('update:modelValue', out);
    emit('change', out);
    emit('input', out);
  },
});
</script>

<template>
  <BFormTextarea
    :id="id || name"
    v-model="modelProxy"
    :name="name"
    :rows="rows"
    :max-rows="maxRows"
    :size="size"
    :placeholder="placeholder"
    :disabled="isDisabled"
    :readonly="isReadonly"
    :autocomplete="autocompleteAttr"
    :debounce="debounce"
    :class="textareaClass"
    :style="{ '--itp-textarea-rows': rows }"
    @blur="touch"
  />
</template>
